import { tapJournal } from '../journal/journalSync';
import { api } from './http';
import type {
  User,
  UserDocument,
  UserDocumentType,
  PilotChecks,
  NotificationsResponse,
} from '../types';

export type ClubMemberRole =
  | 'owner'
  | 'pilot'
  | 'admin'
  | 'moderator'
  | 'technician'
  | 'guest';

export const getUsers = (
  includeInactive = false,
  aircraftId?: number | null
) =>
  api.get<User[]>('/users', {
    params: {
      ...(includeInactive ? { include_inactive: true } : {}),
      ...(aircraftId != null && aircraftId > 0
        ? { aircraft_id: aircraftId }
        : {}),
    },
  });

export const createUser = (data: {
  name: string;
  login: string;
  password: string;
  role?: ClubMemberRole;
  joined_on?: string;
  ownership_share?: number | null;
}) => api.post<User>('/users', data);

export const deactivateUser = (userId: number) =>
  api.put<{ id: number; role: string; updated: boolean }>(
    `/users/${userId}/deactivate`
  );

export const activateUser = (
  userId: number,
  role: ClubMemberRole = 'pilot'
) =>
  api.put<{ id: number; role: string; updated: boolean }>(
    `/users/${userId}/activate`,
    null,
    { params: { role } }
  );

export const changeMemberRole = (userId: number, role: ClubMemberRole) =>
  api.put<{ id: number; role: string; updated: boolean }>(
    `/users/${userId}/role`,
    { role }
  );

export const updateMemberJoinedOn = (userId: number, joined_on: string) =>
  api.put<{ id: number; joined_on: string; updated: boolean }>(
    `/users/${userId}/joined-on`,
    { joined_on }
  );

export const updateOwnershipShares = (data: {
  aircraft_id: number;
  shares: { user_id: number; ownership_share: number }[];
  effective_from?: string;
  deactivate_user_id?: number;
  restore_role?: 'pilot' | 'admin';
  role_change_user_id?: number;
  role_change_to?: ClubMemberRole;
  owner_is_pilot?: boolean;
  aircraft_shares?: {
    aircraft_id: number;
    shares: { user_id: number; ownership_share: number }[];
    effective_from?: string;
  }[];
}) =>
  tapJournal(
    api.put<{
      shares: {
        id: number;
        ownership_share: number;
        ownership_share_from?: string;
      }[];
      updated: boolean;
      deactivated_id?: number;
      role_changed_id?: number;
      role?: string;
      owner_is_pilot?: boolean;
    }>('/users/ownership-shares', data)
  );

export type OwnershipSharePeriod = {
  effective_from: string;
  effective_until: string | null;
  shares: { user_id: number; name: string; ownership_share: number }[];
};

export const getOwnershipShareHistory = (aircraftId: number) =>
  api.get<{ aircraft_id: number; periods: OwnershipSharePeriod[] }>(
    '/users/ownership-shares',
    { params: { aircraft_id: aircraftId } }
  );

export const deleteOwnershipSharePeriod = (
  aircraftId: number,
  effectiveFrom: string
) =>
  tapJournal(
    api.delete<{
      aircraft_id: number;
      effective_from: string;
      deleted: boolean;
    }>('/users/ownership-shares', {
      params: { aircraft_id: aircraftId, effective_from: effectiveFrom },
    })
  );

export const getPilotChecks = (userId: number) =>
  api.get<PilotChecks>(`/users/${userId}/pilot-checks`);

export const updatePilotChecks = (
  userId: number,
  data: {
    birth_date?: string | null;
    ppl_date?: string | null;
    vlek_date?: string | null;
    annual_exam_date?: string | null;
    skills_check_date?: string | null;
  }
) => api.put<PilotChecks>(`/users/${userId}/pilot-checks`, data);

export const passPilotCheck = (
  userId: number,
  check_type: 'ppl' | 'vlek' | 'annual' | 'skills',
  check_date: string
) =>
  api.put<PilotChecks>(`/users/${userId}/pilot-checks/pass`, {
    check_type,
    check_date,
  });

export const getUserDocuments = (userId: number) =>
  api.get<UserDocument[]>(`/users/${userId}/documents`);

export const uploadUserDocument = (
  userId: number,
  file: File,
  doc_type: UserDocumentType,
  group_id?: string
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_type', doc_type);
  if (group_id) {
    form.append('group_id', group_id);
  }
  return api.post<UserDocument>(`/users/${userId}/documents`, form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      },
    ],
  });
};

export const getUserDocumentFile = (userId: number, documentId: number) =>
  api.get<Blob>(`/users/${userId}/documents/${documentId}/file`, {
    responseType: 'blob',
  });

export const downloadUserDocument = async (
  userId: number,
  documentId: number,
  filename: string
) => {
  const res = await getUserDocumentFile(userId, documentId);
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteUserDocument = (userId: number, documentId: number) =>
  api.delete<{ deleted: boolean; id: number }>(
    `/users/${userId}/documents/${documentId}`
  );

export const getNotifications = () =>
  api.get<NotificationsResponse>('/users/notifications');

export const updateUserColor = (userId: number, color: string) =>
  api.put<{ id: number; color: string | null; hide_colors: boolean; updated: boolean }>(
    `/users/${userId}/color`,
    null,
    { params: { color } }
  );

export const updateUserHideColors = (userId: number, hide_colors: boolean) =>
  api.put<{ id: number; color: string | null; hide_colors: boolean; updated: boolean }>(
    `/users/${userId}/hide-colors`,
    null,
    { params: { hide_colors } }
  );

export const updateUserDateFormat = (
  userId: number,
  date_format: 'dot' | 'long'
) =>
  api.put<{ id: number; date_format: 'dot' | 'long'; updated: boolean }>(
    `/users/${userId}/date-format`,
    null,
    { params: { date_format } }
  );

export const changeUserPassword = (
  userId: number,
  current_password: string,
  new_password: string
) =>
  api.put<{ id: number; updated: boolean }>(`/users/${userId}/password`, {
    current_password,
    new_password,
  });

export const updateUserProfile = (
  userId: number,
  data: { name: string; login: string }
) =>
  api.put<{ id: number; name: string; login: string; updated: boolean }>(
    `/users/${userId}/profile`,
    data
  );
